import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { salesData } from './data/salesData';

function App() {
  const [data, setData] = useState({
    galleries: [],
    artists: [],
    galleriesByCount: [],
    artistsByCount: [],
    galleryAverages: [],
    artistAverages: [],
    combinedGalleryRankings: [],  // New state for combined gallery performance
    combinedArtistRankings: []    // New state for combined artist performance
  });

  useEffect(() => {
    const extractPrice = (priceStr) => {
      priceStr = priceStr.toString().replace(/,/g, '');
      
      if (priceStr.includes('–') || priceStr.includes('-')) {
        const [min, max] = priceStr.split(/–|-/).map(p => 
          parseFloat(p.replace(/[^\d.]/g, ''))
        );
        return (min + max) / 2;
      }
      
      if (priceStr.includes('million')) {
        const num = parseFloat(priceStr.match(/[\d.]+/)[0]);
        return num * 1000000;
      }
      
      const match = priceStr.match(/[\d.]+/);
      return match ? parseFloat(match[0]) : 0;
    };

    const calculateAveragesAndRankings = (totals, counts) => {
      const rankings = {};
      Object.keys(totals).forEach(name => {
        if (counts[name] > 0) {
          const averagePrice = totals[name] / counts[name];
          rankings[name] = {
            totalSales: totals[name],
            worksCount: counts[name],
            averagePrice: averagePrice
          };
        }
      });

      return Object.entries(rankings)
        .map(([name, stats]) => ({
          name,
          value: stats.averagePrice / 1000000,  // Average price in millions
          totalSales: stats.totalSales / 1000000,
          worksCount: stats.worksCount,
          averagePrice: stats.averagePrice / 1000000
        }))
        .sort((a, b) => (b.averagePrice * Math.log(b.worksCount + 1)) - 
                        (a.averagePrice * Math.log(a.worksCount + 1)));
    };

    const processData = () => {
      const sales = [];
      const lines = salesData.split('\n');

      // Initialize totals objects
      const galleryTotals = {};
      const galleryCount = {};
      const artistTotals = {};
      const artistCount = {};

      for (const line of lines) {
        if (line.startsWith('$')) {
          const match = line.match(/\$([\d,.\–\- million]+): ([^,]+), .* at ([^,\n]+)/);
          
          if (match) {
            const [_, price, artist, gallery] = match;
            sales.push({
              price: extractPrice(price),
              artist: artist.trim(),
              gallery: gallery.trim()
            });
          }
        }
      }

      // Calculate totals
      sales.forEach(sale => {
        galleryTotals[sale.gallery] = (galleryTotals[sale.gallery] || 0) + sale.price;
        galleryCount[sale.gallery] = (galleryCount[sale.gallery] || 0) + 1;
        artistTotals[sale.artist] = (artistTotals[sale.artist] || 0) + sale.price;
        artistCount[sale.artist] = (artistCount[sale.artist] || 0) + 1;
      });

      // Calculate combined rankings
      const combinedGalleryRankings = calculateAveragesAndRankings(galleryTotals, galleryCount);
      const combinedArtistRankings = calculateAveragesAndRankings(artistTotals, artistCount);

      setData({
        galleries: Object.entries(galleryTotals)
          .sort(([,a], [,b]) => b - a)
          .slice(0, 20)
          .map(([name, value]) => ({
            name,
            value: value / 1000000,
            count: galleryCount[name]
          })),
        artists: Object.entries(artistTotals)
          .sort(([,a], [,b]) => b - a)
          .slice(0, 20)
          .map(([name, value]) => ({
            name,
            value: value / 1000000,
            count: artistCount[name]
          })),
        galleriesByCount: Object.entries(galleryCount)
          .sort(([,a], [,b]) => b - a)
          .slice(0, 20)
          .map(([name, value]) => ({
            name,
            value,
            totalSales: galleryTotals[name] / 1000000
          })),
        artistsByCount: Object.entries(artistCount)
          .sort(([,a], [,b]) => b - a)
          .slice(0, 20)
          .map(([name, value]) => ({
            name,
            value,
            totalSales: artistTotals[name] / 1000000
          })),
        combinedGalleryRankings: combinedGalleryRankings.slice(0, 20),
        combinedArtistRankings: combinedArtistRankings.slice(0, 20)
      });
    };

    processData();
  }, []);

  const CombinedRankingTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div style={{ backgroundColor: 'white', padding: '10px', border: '1px solid #ccc' }}>
          <p><strong>{label}</strong></p>
          <p>Average Price: ${data.averagePrice.toFixed(2)}M</p>
          <p>Works Sold: {data.worksCount}</p>
          <p>Total Sales: ${data.totalSales.toFixed(2)}M</p>
        </div>
      );
    }
    return null;
  };

  const BasicTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div style={{ backgroundColor: 'white', padding: '10px', border: '1px solid #ccc' }}>
          <p><strong>{label}</strong></p>
          <p>{payload[0].name}: {typeof payload[0].value === 'number' ? 
            (payload[0].name === "Total Sales" ? 
              `$${payload[0].value.toFixed(2)}M` : 
              payload[0].value
            ) : payload[0].value}
          </p>
          {data.count && <p>Works Sold: {data.count}</p>}
          {data.totalSales && <p>Total Sales: ${data.totalSales.toFixed(2)}M</p>}
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{ padding: '20px' }}>
      <h1 style={{ marginBottom: '20px' }}>Art Basel Miami Beach 2024 Sales Analysis</h1>
      
      <div style={{ marginBottom: '40px' }}>
        <h2>Top Galleries by Sales Value (in millions USD)</h2>
<p>This table is derived from the recent artnet.com article (https://news.artnet.com/market/art-miami-miami-beach-sales-2583386)  which listed the top art sales of Art Basel Miami 2024.</p>
        <div style={{ height: '800px' }}>
          <ResponsiveContainer>
            <BarChart data={data.galleries} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={200} />
              <Tooltip content={<BasicTooltip />} />
              <Bar dataKey="value" fill="#8884d8" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div style={{ marginBottom: '40px' }}>
        <h2>Top Artists by Sales Value (in millions USD)</h2>
        <div style={{ height: '800px' }}>
          <ResponsiveContainer>
            <BarChart data={data.artists} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={200} />
              <Tooltip content={<BasicTooltip />} />
              <Bar dataKey="value" fill="#82ca9d" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div style={{ marginBottom: '40px' }}>
        <h2>Top Galleries by Number of Works Sold</h2>
        <div style={{ height: '800px' }}>
          <ResponsiveContainer>
            <BarChart data={data.galleriesByCount} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={200} />
              <Tooltip content={<BasicTooltip />} />
              <Bar dataKey="value" fill="#8884d8" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div style={{ marginBottom: '40px' }}>
        <h2>Top Artists by Number of Works Sold</h2>
        <div style={{ height: '800px' }}>
          <ResponsiveContainer>
            <BarChart data={data.artistsByCount} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={200} />
              <Tooltip content={<BasicTooltip />} />
              <Bar dataKey="value" fill="#82ca9d" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div style={{ marginBottom: '40px' }}>
        <h2>Combined Gallery Performance Rankings (Price/Volume Balance)</h2>
        <div style={{ height: '800px' }}>
          <ResponsiveContainer>
            <BarChart data={data.combinedGalleryRankings} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={200} />
              <Tooltip content={<CombinedRankingTooltip />} />
              <Bar dataKey="value" fill="#9370db" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div style={{ marginBottom: '40px' }}>
        <h2>Combined Artist Performance Rankings (Price/Volume Balance)</h2>
        <div style={{ height: '800px' }}>
          <ResponsiveContainer>
            <BarChart data={data.combinedArtistRankings} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={200} />
              <Tooltip content={<CombinedRankingTooltip />} />
              <Bar dataKey="value" fill="#20b2aa" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

export default App;